import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Loader2, Ship, Copy } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { STATUS_META, STATUS_KEYS, StatusBadge } from "@/components/RequirementStatus";
import { VesselFormDialog } from "@/pages/Vessels";
import { VesselDocuments } from "@/components/VesselDocuments";
import { GenerateSea } from "@/components/GenerateSea";
import { OTHER, OtherRankInput, isOther, otherText, selValue } from "@/lib/ranks";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const UNASSIGNED = "__none__";

function StatusSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-48" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        {STATUS_KEYS.map((k) => (
          <SelectItem key={k} value={k} data-testid={`${testid}-option-${k}`}>{STATUS_META[k].label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function RequirementDialog({ open, onOpenChange, vesselId, req, onSaved }) {
  const [ranks, setRanks] = useState([]);
  const [users, setUsers] = useState([]);
  const blankRow = { rank: "", qty: 1, joining_date: "", joining_port: "" };
  const [picked, setPicked] = useState([blankRow]);
  const [form, setForm] = useState({ rank: "", required_count: 1, assigned_to_user_id: UNASSIGNED, status: "open", joining_date: "", joining_port: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      rank: req?.rank || "",
      required_count: req?.required_count || 1,
      assigned_to_user_id: req?.assigned_to_user_id || UNASSIGNED,
      status: req?.status || "open",
      joining_date: req?.joining_date || "",
      joining_port: req?.joining_port || "",
    });
    setPicked([blankRow]);
    Promise.all([api.get("/master-data", { params: { kind: "rank" } }), api.get("/vessels/sourcing-users")])
      .then(([r, u]) => { setRanks(r.data); setUsers(u.data); })
      .catch((err) => toast.error(formatApiError(err)));
  }, [open, req]);

  const submit = async (e) => {
    e.preventDefault();
    if ((req ? [form.rank] : picked.map((p) => p.rank)).some((r) => isOther(r) && otherText(r).trim().length < 2)) { toast.error("Type the custom rank name for Others (at least 2 characters)"); return; }
    const rows = picked.filter((p) => p.rank);
    if (req ? !form.rank : !rows.length) { toast.error("Pick at least one rank"); return; }
    if (!req && rows.some((p) => !(Number(p.qty) >= 1))) { toast.error("Quantity must be at least 1"); return; }
    setSaving(true);
    const body = {
      ...form,
      required_count: Number(form.required_count),
      assigned_to_user_id: form.assigned_to_user_id === UNASSIGNED ? null : form.assigned_to_user_id,
    };
    try {
      if (req) {
        await api.patch(`/requirements/${req.id}`, body);
        toast.success("Crew requirement updated");
      } else {
        const failed = [];
        for (const p of rows) {
          try { await api.post(`/vessels/${vesselId}/requirements`, { ...body, rank: p.rank, required_count: Number(p.qty), joining_date: p.joining_date || null, joining_port: p.joining_port.trim() || null }); }
          catch (err) { failed.push(`${p.rank}: ${formatApiError(err)}`); }
        }
        const ok = rows.length - failed.length;
        if (ok) toast.success(`${ok} crew requirement${ok === 1 ? "" : "s"} added`);
        failed.forEach((f) => toast.error(f));
        if (failed.length) { setPicked(rows.filter((p) => failed.some((f) => f.startsWith(`${p.rank}:`)))); onSaved(); return; }
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={req ? undefined : "sm:max-w-4xl max-h-[90vh] overflow-y-auto"} data-testid="requirement-form-dialog">
        <DialogHeader>
          <DialogTitle>{req ? "Edit crew requirement" : "Add crew requirement"}</DialogTitle>
          <DialogDescription>{req ? "Each rank can appear once per vessel and go to one Sourcing user." : "Add one row per rank with its quantity, joining date and joining port. Each row becomes its own line in the Crew Requirements table."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {req ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Rank</Label>
              <Select value={selValue(form.rank)} onValueChange={(v) => setForm({ ...form, rank: v })}>
                <SelectTrigger data-testid="requirement-rank-select"><SelectValue placeholder="Select rank" /></SelectTrigger>
                <SelectContent>
                  {ranks.map((r) => <SelectItem key={r.id} value={r.value} data-testid={`requirement-rank-option-${r.value}`}>{r.value}</SelectItem>)}
                </SelectContent>
              </Select>
              <OtherRankInput value={form.rank} onChange={(v) => setForm({ ...form, rank: v })} testid="requirement-rank-other-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="req-count">Required</Label>
              <Input id="req-count" type="number" min={1} max={999} required value={form.required_count}
                onChange={(e) => setForm({ ...form, required_count: e.target.value })} data-testid="requirement-count-input" />
            </div>
          </div>
          ) : (
          <div className="space-y-2" data-testid="requirement-rows">
            <div className="hidden md:grid md:grid-cols-[1fr_80px_150px_1fr_36px] gap-2 text-sm font-medium text-slate-700"><span>Rank</span><span>Quantity</span><span>Joining date</span><span>Joining port</span><span /></div>
            {picked.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px] md:grid-cols-[1fr_80px_150px_1fr_36px] gap-2 items-start rounded-lg border border-slate-100 p-2 md:border-0 md:p-0" data-testid={`requirement-row-input-${i}`}>
                <div>
                <Select value={selValue(p.rank)} onValueChange={(v) => setPicked(picked.map((x, j) => (j === i ? { ...x, rank: v } : x)))}>
                  <SelectTrigger data-testid={`requirement-row-rank-${i}`}><SelectValue placeholder="Select rank" /></SelectTrigger>
                  <SelectContent>
                    {ranks.map((r) => <SelectItem key={r.id} value={r.value}>{r.value}</SelectItem>)}
                  </SelectContent>
                </Select>
                <OtherRankInput value={p.rank} onChange={(v) => setPicked(picked.map((x, j) => (j === i ? { ...x, rank: v } : x)))} testid={`requirement-row-other-${i}`} />
                </div>
                <Input type="number" min={1} max={999} value={p.qty} onChange={(e) => setPicked(picked.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} aria-label="Quantity" data-testid={`requirement-row-qty-${i}`} />
                <Input type="date" className="col-span-2 md:col-span-1" value={p.joining_date} onChange={(e) => setPicked(picked.map((x, j) => (j === i ? { ...x, joining_date: e.target.value } : x)))} aria-label="Joining date" data-testid={`requirement-row-joining-date-${i}`} />
                <Input className="col-span-2 md:col-span-1" value={p.joining_port} placeholder="Joining port" onChange={(e) => setPicked(picked.map((x, j) => (j === i ? { ...x, joining_port: e.target.value } : x)))} aria-label="Joining port" data-testid={`requirement-row-joining-port-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="col-span-2 md:col-span-1 justify-self-end" disabled={picked.length === 1} onClick={() => setPicked(picked.filter((_, j) => j !== i))} aria-label="Remove row" data-testid={`requirement-row-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
                {i > 0 && (
                  <Button type="button" variant="link" size="sm" className="col-span-2 md:col-span-5 justify-self-start h-auto p-0 text-xs" onClick={() => setPicked(picked.map((x, j) => (j === i ? { ...x, joining_date: picked[i - 1].joining_date, joining_port: picked[i - 1].joining_port } : x)))} data-testid={`requirement-row-copy-${i}`}>
                    <Copy className="w-3 h-3 mr-1" /> Copy date & port
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setPicked([...picked, blankRow])} data-testid="requirement-add-row-btn"><Plus className="w-4 h-4 mr-1" /> Add Rank</Button>
          </div>
          )}
          {req && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="req-joining-date">Joining date</Label>
              <Input id="req-joining-date" type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="requirement-joining-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="req-joining-port">Joining port</Label>
              <Input id="req-joining-port" value={form.joining_port} placeholder="e.g. Singapore" onChange={(e) => setForm({ ...form, joining_port: e.target.value })} data-testid="requirement-joining-port-input" />
            </div>
          </div>
          )}
          <div className="space-y-1.5">
            <Label>{req ? "Assigned Sourcing user" : "Assign all rows to (optional, editable per row later)"}</Label>
            <Select value={form.assigned_to_user_id} onValueChange={(v) => setForm({ ...form, assigned_to_user_id: v })}>
              <SelectTrigger data-testid="requirement-assignee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED} data-testid="requirement-assignee-option-none">Unassigned</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`requirement-assignee-option-${u.id}`}>{u.name} · {u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <StatusSelect value={form.status} onChange={(v) => setForm({ ...form, status: v })} testid="requirement-status-select" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="requirement-form-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : req ? "Save changes" : "Add crew requirement"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDelete({ target, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!target} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="confirm-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{target?.title}</AlertDialogTitle>
          <AlertDialogDescription>{target?.body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-delete-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-confirm-btn">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AssigneeCell({ r, users, onAssign }) {
  if (!users) {
    return r.assigned_to_user_name ? (
      <div><div className="text-slate-800">{r.assigned_to_user_name}</div><div className="text-xs text-slate-400">{r.assigned_to_user_email}</div></div>
    ) : <span className="text-slate-400 italic">Unassigned</span>;
  }
  return (
    <Select value={r.assigned_to_user_id || UNASSIGNED} onValueChange={(v) => onAssign(r, v === UNASSIGNED ? null : v)}>
      <SelectTrigger className="h-8 w-52 text-sm" data-testid={`requirement-assignee-inline-${r.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
        {users.map((u) => <SelectItem key={u.id} value={u.id} title={u.email}>{u.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const istToday = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
function DaysNote({ r }) {
  if (!r.joining_date) return null;
  const d = Math.round((new Date(r.joining_date) - new Date(istToday())) / 864e5);
  if (d < 0 && r.status !== "open") return null;
  const text = d < 0 ? `${-d} day${d === -1 ? "" : "s"} overdue` : d === 0 ? "today" : `in ${d} day${d === 1 ? "" : "s"}`;
  return <span className={`ml-2 text-xs ${d < 0 ? "text-rose-600 font-semibold" : "text-slate-500"}`} data-testid={`requirement-days-note-${r.id}`}>{text}</span>;
}

const daysTo = (r) => Math.round((new Date(r.joining_date) - new Date(istToday())) / 864e5);
const reqGroup = (r) => (r.status !== "open" || !r.joining_date ? 2 : daysTo(r) < 0 ? 0 : daysTo(r) <= 14 ? 1 : 2);
const sortReqs = (list) => [...list].sort((a, b) => reqGroup(a) - reqGroup(b) || (a.joining_date || "9999").localeCompare(b.joining_date || "9999"));

function RequirementRow({ r, canManage, canDelete, canEditStatus, onStatus, onEdit, onDelete, users, onAssign }) {
  return (
    <tr className={`border-t border-slate-100 ${["bg-rose-50", "bg-orange-50", ""][reqGroup(r)]}`} data-urgency={["overdue", "urgent", "normal"][reqGroup(r)]} data-testid={`requirement-row-${r.id}`}>
      <td className="px-5 py-3.5 font-semibold text-slate-800" data-testid={`requirement-rank-${r.id}`}>{r.rank}</td>
      <td className="px-5 py-3.5 font-mono text-slate-700" data-testid={`requirement-count-${r.id}`}>{r.required_count}</td>
      <td className="px-5 py-3.5 text-slate-700 whitespace-nowrap" data-testid={`requirement-joining-date-${r.id}`}>
        {r.joining_date ? new Date(`${r.joining_date}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : <span className="text-slate-400">—</span>}
        <DaysNote r={r} />
      </td>
      <td className="px-5 py-3.5 text-slate-700" data-testid={`requirement-joining-port-${r.id}`}>
        {r.joining_port || <span className="text-slate-400">—</span>}
      </td>
      <td className="px-5 py-3.5" data-testid={`requirement-assignee-${r.id}`}>
        <AssigneeCell r={r} users={canManage ? users : null} onAssign={onAssign} />
      </td>
      <td className="px-5 py-3.5">
        {canEditStatus ? (
          <StatusSelect value={r.status} onChange={(v) => onStatus(r, v)} testid={`requirement-status-select-${r.id}`} />
        ) : <StatusBadge status={r.status} testid={`requirement-status-${r.id}`} />}
      </td>
      <td className="px-5 py-3.5 text-right whitespace-nowrap">
        {canManage && (
          <>
            <Button size="icon" variant="ghost" onClick={() => onEdit(r)} data-testid={`requirement-edit-btn-${r.id}`}><Pencil className="w-4 h-4" /></Button>
            {canDelete && <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => onDelete(r)} data-testid={`requirement-delete-btn-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
          </>
        )}
      </td>
    </tr>
  );
}

export default function VesselDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission("vessels.manage");
  const canDelete = canManage && ["Super Admin", "Management/HOD"].includes(user?.role?.role_name);
  const [vessel, setVessel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editVessel, setEditVessel] = useState(false);
  const [reqDialog, setReqDialog] = useState({ open: false, req: null });
  const [confirm, setConfirm] = useState(null);
  const [tab, setTab] = useState("ranks");

  const load = async () => {
    try {
      const { data } = await api.get(`/vessels/${id}`);
      setVessel(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setVessel(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const [srcUsers, setSrcUsers] = useState([]);
  useEffect(() => { if (canManage) api.get("/vessels/sourcing-users").then(({ data }) => setSrcUsers(data)).catch(() => {}); }, [canManage]);
  const assign = async (r, uid) => {
    try {
      await api.patch(`/requirements/${r.id}`, { assigned_to_user_id: uid });
      toast.success(`${r.rank} assigned to ${uid ? srcUsers.find((u) => u.id === uid)?.name : "nobody"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const canEditStatus = (r) => canManage || (hasPermission("requirements.manage") && r.assigned_to_user_id === user?.id);

  const changeStatus = async (r, status) => {
    try {
      await api.patch(`/requirements/${r.id}/status`, { status });
      toast.success(`${r.rank} → ${STATUS_META[status].label}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const runDelete = async () => {
    const t = confirm;
    setConfirm(null);
    try {
      await api.delete(t.url);
      toast.success("Deleted");
      if (t.kind === "vessel") navigate("/vessels");
      else load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline" /></div>;
  if (!vessel) {
    return (
      <div className="py-20 text-center" data-testid="vessel-not-found">
        <Ship className="w-8 h-8 text-slate-300 mx-auto" />
        <div className="text-sm text-slate-500 mt-2">Vessel not found or not available to you.</div>
        <Link to="/vessels" className="text-sm text-cyan-700 hover:underline mt-3 inline-block">Back to vessels</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vessel-detail-page">
      <Link to="/vessels" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors" data-testid="vessel-back-link">
        <ArrowLeft className="w-4 h-4" /> All vessels
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Vessel</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }} data-testid="vessel-detail-name">
            {vessel.vessel_name}
          </h1>
          <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <span data-testid="vessel-detail-owner">Owner: <span className="text-slate-700">{vessel.owner_name}</span></span>
            <span className="font-mono" data-testid="vessel-detail-imo">IMO: {vessel.imo_number || "—"}</span>
            <span data-testid="vessel-detail-type">Type: <span className="text-slate-700">{vessel.vessel_type || "—"}</span></span>
          </div>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEditVessel(true)} data-testid="edit-vessel-btn"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
            {canDelete && <Button variant="outline" className="text-rose-600 hover:text-rose-700" data-testid="delete-vessel-btn"
              onClick={() => setConfirm({ kind: "vessel", url: `/vessels/${vessel.id}`, title: `Delete ${vessel.vessel_name}?`, body: `This also removes its ${vessel.rank_count} rank requirement(s). This cannot be undone.` })}>
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>}
            {tab === "ranks" && (
              <Button onClick={() => setReqDialog({ open: true, req: null })} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="add-requirement-btn">
                <Plus className="w-4 h-4 mr-1" /> Add crew requirement
              </Button>
            )}
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="ranks" data-testid="vessel-tab-ranks">Crew Requirements</TabsTrigger>
          {hasPermission("documents.view") && <TabsTrigger value="documents" data-testid="vessel-tab-documents">Documents</TabsTrigger>}
          {(hasPermission("contracts.manage") || user?.can_generate_sea) && <TabsTrigger value="sea" data-testid="vessel-tab-sea">Generate SEA</TabsTrigger>}
        </TabsList>
        <TabsContent value="ranks" className="mt-4 space-y-6">
      <div className="grid grid-cols-3 gap-3 max-w-xl">
        {[["Ranks", vessel.rank_count, "stat-ranks"], ["Crew required", vessel.total_required, "stat-required"], ["Open", vessel.open_count, "stat-open"]].map(([l, n, t]) => (
          <div key={t} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">{l}</div>
            <div className="text-2xl font-bold text-slate-900 font-mono mt-1" data-testid={`vessel-${t}`}>{n}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="requirements-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Rank</th>
                <th className="px-5 py-3 text-left font-medium">Required</th>
                <th className="px-5 py-3 text-left font-medium">Joining date</th>
                <th className="px-5 py-3 text-left font-medium">Joining port</th>
                <th className="px-5 py-3 text-left font-medium">Assigned to</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sortReqs(vessel.requirements).map((r) => (
                <RequirementRow key={r.id} r={r} canManage={canManage} canDelete={canDelete} canEditStatus={canEditStatus(r)} onStatus={changeStatus} users={srcUsers} onAssign={assign}
                  onEdit={(req) => setReqDialog({ open: true, req })}
                  onDelete={(req) => setConfirm({ kind: "req", url: `/requirements/${req.id}`, title: `Remove ${req.rank}?`, body: "This rank requirement will be deleted from the vessel." })} />
              ))}
            </tbody>
          </table>
        </div>
        {vessel.requirements.length === 0 && (
          <div className="py-12 text-center text-sm text-slate-400" data-testid="requirements-empty-state">No ranks added to this vessel yet.</div>
        )}
      </div>
        </TabsContent>
        {hasPermission("documents.view") && (
          <TabsContent value="documents" className="mt-4">
            <VesselDocuments vesselId={vessel.id} />
          </TabsContent>
        )}
        {(hasPermission("contracts.manage") || user?.can_generate_sea) && (
          <TabsContent value="sea" className="mt-4">
            <GenerateSea vesselId={vessel.id} />
          </TabsContent>
        )}
      </Tabs>

      <VesselFormDialog open={editVessel} onOpenChange={setEditVessel} vessel={vessel} onSaved={load} />
      <RequirementDialog open={reqDialog.open} onOpenChange={(o) => setReqDialog((s) => ({ ...s, open: o }))} vesselId={vessel.id} req={reqDialog.req} onSaved={load} />
      <ConfirmDelete target={confirm} onCancel={() => setConfirm(null)} onConfirm={runDelete} />
    </div>
  );
}
